// Permisos por MÓDULO del motor de lealify-base (user_module_override > tenant_role >
// role_defaults, resuelto server-side; aquí solo se consume el nivel efectivo que llega
// en /me/permissions vía currentUser.modules).
//
// COMPLEMENTA a planPermissions (gating por plan Pro/Premium): ambos gates se aplican en
// AND — un módulo puede estar permitido por rol pero seguir candado por plan, y viceversa.
import { User } from '../types/user';

export type ModuleLevel = 'none' | 'view' | 'operate' | 'manage';

const RANK: Record<ModuleLevel, number> = { none: 0, view: 1, operate: 2, manage: 3 };

// Nivel efectivo del operador en un módulo. Sin mapa de módulos (sesión de plataforma o
// contexto viejo) devuelve 'manage': fail-open para no esconder pantallas por un hueco de
// datos — el backend rechaza igual con 403 si de verdad no tiene permiso.
export function moduleLevel(user: User | null, key: string): ModuleLevel {
  if (!user) return 'none';
  if (!user.modules) return 'manage';
  const m = user.modules[key];
  if (!m || !m.active) return 'none';
  return m.level || 'none';
}

// ¿El operador alcanza `min` en el módulo? (view por defecto = "puede ver la pantalla")
export function hasModule(user: User | null, key: string, min: ModuleLevel = 'view'): boolean {
  return RANK[moduleLevel(user, key)] >= RANK[min];
}

// ¿Es el dueño real? (gates "solo el dueño": editar permisos, Ver como, crear owners)
export function isOwner(user: User | null): boolean {
  return user?.baseRole === 'owner';
}

// Mapa ÚNICO pantalla → módulo del motor (F3). Lo consumen el sidebar, el bottom-nav y el
// guard de rutas del Layout: módulo en 'none' = ítem oculto + redirect a /dashboard. Las
// rutas ausentes aquí no se gatean (p. ej. /dashboard, settings de cuenta propia, FAQ).
export const PATH_MODULE: Record<string, string> = {
  '/dashboard/business': 'settings',
  // Clientes
  '/dashboard/clients': 'contacts',
  '/dashboard/register-client': 'contacts',
  '/dashboard/latest-registrations': 'contacts',
  '/dashboard/best-clients': 'contacts',
  '/dashboard/inactive-clients': 'contacts',
  '/dashboard/unsatisfied-clients': 'contacts',
  '/dashboard/tags': 'contacts',
  '/dashboard/birthday-panel': 'birthdays',
  '/dashboard/referrals': 'loyalty',
  // Operaciones (lealtad)
  '/dashboard/latest-visits': 'loyalty',
  '/dashboard/recharge-points': 'loyalty',
  '/dashboard/rewards': 'loyalty',
  '/dashboard/redeem-rewards': 'loyalty',
  '/dashboard/memberships': 'loyalty',   // Membresías F1 (20-oct-2026)
  '/dashboard/gift-cards': 'giftcards',
  '/dashboard/gift-card-config': 'giftcards',
  // Comunicación (mensajes por evento + WhatsApp viven bajo el módulo templates en base)
  '/dashboard/campaigns': 'campaigns',
  '/dashboard/tasks': 'tasks',
  '/dashboard/mensajes': 'templates',   // Mensajes automáticos (F2 reorden 24-oct-2026); las 8 rutas de abajo redirigen ahí
  '/dashboard/repartos': 'delivery',               // repartos a domicilio (23-oct-2026)
  '/dashboard/repartos/corte': 'delivery',
  '/dashboard/repartos/configuracion': 'delivery',
  '/dashboard/welcome-message': 'templates',
  '/dashboard/visit-message': 'templates',
  '/dashboard/referral-message': 'templates',
  '/dashboard/feedback-alert': 'templates',
  '/dashboard/low-balance-alert': 'templates',
  '/dashboard/membership-expiring': 'templates',
  '/dashboard/membership-expired': 'templates',
  '/dashboard/google-reviews': 'templates',
  '/dashboard/whatsapp': 'templates',
  // Análisis / cuenta
  '/dashboard/analytics': 'reports',
  '/dashboard/staff-report': 'loyalty',   // «Por empleado» (23-oct-2026); además solo dueño/gerente (nav + backend)
  '/dashboard/subscription': 'billing',
  '/dashboard/legal': 'settings',   // redirige a Mi Negocio › Kiosco (F1 reorden 24-oct-2026)
  '/dashboard/daily-cut': 'reports',   // Corte diario (F1 reorden 24-oct-2026); además solo dueño (nav + el componente)
  // Equipo
  '/dashboard/team': 'users_admin',
};

// Pantallas de CONFIGURACIÓN: además de tener el módulo, hace falta nivel «Administrar».
// Un Empleado (loyalty/giftcards 'operate', templates 'view') registra, da puntos y canjea,
// pero no debe ver siquiera dónde se cambia cómo se premia, los mensajes o la conexión de
// WhatsApp: el backend ya rechazaba con 403, pero la pantalla se abría con sus botones y
// parecía editable (24-oct-2026, Hogar China City: «que respeten lo que tengo yo»).
// Las rutas que no están aquí piden 'view', como siempre.
export const PATH_MIN_LEVEL: Record<string, ModuleLevel> = {
  '/dashboard/rewards': 'manage',            // Recompensas (crear/editar); canjear es otra pantalla
  '/dashboard/memberships': 'manage',
  '/dashboard/gift-card-config': 'manage',
  '/dashboard/mensajes': 'manage',
  '/dashboard/welcome-message': 'manage',
  '/dashboard/visit-message': 'manage',
  '/dashboard/referral-message': 'manage',
  '/dashboard/feedback-alert': 'manage',
  '/dashboard/low-balance-alert': 'manage',
  '/dashboard/membership-expiring': 'manage',
  '/dashboard/membership-expired': 'manage',
  '/dashboard/google-reviews': 'manage',
  '/dashboard/whatsapp': 'manage',           // vincular/desvincular el WhatsApp del negocio
};

// ¿Puede abrir esta pantalla? Lo usan el sidebar, el bottom-nav y el guard del Layout:
// módulo del mapa PATH_MODULE (o el que pase el ítem) al nivel mínimo de PATH_MIN_LEVEL.
export function canOpenPath(user: User | null, path: string, mod: string | undefined = PATH_MODULE[path]): boolean {
  if (!mod) return true;
  return hasModule(user, mod, PATH_MIN_LEVEL[path] ?? 'view');
}
